import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { invalidateCache } from "@/lib/cache";
import { useAuth } from "@/context/AuthContext";
import { EventStatusPill, BookingStatusPill, ScorePill } from "@/components/Shared";
import EventForm from "@/pages/staff/EventForm";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { ArrowLeft, CheckCircle2, Circle, Phone, Mail, MessageSquare, UserPlus, Clock, Printer, Trash2 } from "lucide-react";

function fmtLong(d) {
  if (!d) return "";
  return new Date(d + "T00:00:00").toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
}

function waLink(mobile) {
  const digits = (mobile || "").replace(/\D/g, "");
  if (!digits) return null;
  return `https://wa.me/${digits.startsWith("0") ? "44" + digits.slice(1) : digits}`;
}

function ActivityChip({ label, done, href, testid }) {
  const inner = (
    <span className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-sm border text-[10px] font-mono uppercase tracking-widest ${
      done ? "bg-emerald-100 border-emerald-300 text-emerald-800" : "bg-elevated border-steel text-slate-500"
    }`}>
      {done ? <CheckCircle2 className="h-3 w-3" /> : <Circle className="h-3 w-3" />}
      {label}
    </span>
  );
  return href ? <Link to={href} data-testid={testid}>{inner}</Link> : <span data-testid={testid}>{inner}</span>;
}

export default function EventDetail() {
  const { eventId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const isManagement = user?.role === "management_admin";
  const [event, setEvent] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [bookOpen, setBookOpen] = useState(false);
  const [candidates, setCandidates] = useState([]);
  const [bookCandidate, setBookCandidate] = useState("");
  const [rescheduleFor, setRescheduleFor] = useState(null);
  const [rescheduleEvent, setRescheduleEvent] = useState("");
  const [openEvents, setOpenEvents] = useState([]);
  const [delOpen, setDelOpen] = useState(false);
  const [delReason, setDelReason] = useState("");
  const [delNotes, setDelNotes] = useState("");
  const [delWarning, setDelWarning] = useState(null);
  const [delBusy, setDelBusy] = useState(false);

  const load = () => api.get(`/admin/events/${eventId}`).then((r) => setEvent(r.data)).catch(() => toast.error("Could not load assessment day"));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [eventId]);

  if (!event) return <div className="h-8 w-8 border-4 border-steel border-t-lomond rounded-full animate-spin" data-testid="event-loading" />;

  const action = async (bookingId, act, extra = {}) => {
    try {
      await api.post(`/admin/bookings/${bookingId}/action`, { action: act, ...extra });
      toast.success("Updated.");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const openBookDialog = async () => {
    const { data } = await api.get("/staff/candidates");
    setCandidates(data.filter((c) => c.role === "applicant" && !["declined"].includes(c.stage)));
    setBookOpen(true);
  };

  const manualBook = async (force = false) => {
    if (!bookCandidate) { toast.error("Choose a candidate."); return; }
    try {
      await api.post(`/admin/events/${eventId}/book`, { candidate_id: bookCandidate, force });
      toast.success("Candidate booked and confirmation sent to their portal.");
      setBookOpen(false); setBookCandidate("");
      load();
    } catch (e) {
      if (e?.response?.status === 409) {
        if (window.confirm("This assessment day is FULL. Override capacity and book anyway?")) return manualBook(true);
      } else toast.error(apiError(e));
    }
  };

  const openReschedule = async (booking) => {
    const { data } = await api.get("/admin/events");
    setOpenEvents(data.filter((e) => e.effective_status === "open" && e.id !== eventId));
    setRescheduleFor(booking);
  };

  const submitReschedule = async () => {
    if (!rescheduleEvent) { toast.error("Choose a new date."); return; }
    await action(rescheduleFor.id, "reschedule", { event_id: rescheduleEvent });
    setRescheduleFor(null); setRescheduleEvent("");
  };

  const setStatus = async (status) => {
    if (status === "cancelled" && !window.confirm("Cancel this assessment day? All bookings will be cancelled and candidates notified.")) return;
    try {
      await api.post(`/admin/events/${eventId}/status`, { status });
      toast.success("Status updated.");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const openDelete = () => { setDelOpen(true); setDelReason(""); setDelNotes(""); setDelWarning(null); };

  const submitDelete = async (force = false) => {
    if (!delReason) { toast.error("Select a reason."); return; }
    if (delReason === "other" && !delNotes.trim()) { toast.error("Notes are required for Other."); return; }
    setDelBusy(true);
    try {
      await api.delete(`/admin/events/${eventId}`, { data: { reason: delReason, notes: delNotes, confirm_delete_bookings: force } });
      toast.success("Assessment day deleted.");
      invalidateCache("/admin/events");
      navigate("/staff/calendar");
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (e?.response?.status === 409 && typeof d === "object") setDelWarning(d);
      else toast.error(apiError(e));
    } finally { setDelBusy(false); }
  };

  const arrivedOrBooked = event.attendees.filter((a) => ["arrived", "booked"].includes(a.status));
  const arrivedCount = event.attendees.filter((a) => ["arrived", "completed"].includes(a.status)).length;

  return (
    <div className="space-y-6" data-testid="event-detail-page">
      <div className="flex items-center gap-3 flex-wrap">
        <Button variant="outline" size="sm" onClick={() => navigate("/staff/calendar")} className="border-steel text-charcoal bg-white" data-testid="back-to-calendar">
          <ArrowLeft className="h-4 w-4 mr-1" /> Calendar
        </Button>
        <Button variant="outline" size="sm" onClick={() => window.print()} className="border-steel text-charcoal bg-white" data-testid="print-day-sheet-btn">
          <Printer className="h-4 w-4 mr-1" /> Print Day Sheet
        </Button>
        {isManagement && (
          <>
            <Button variant="outline" size="sm" onClick={() => setEditOpen(true)} className="border-steel text-charcoal bg-white" data-testid="edit-event-btn">Edit Event</Button>            {event.status === "open" && <Button variant="outline" size="sm" onClick={() => setStatus("closed")} className="border-steel text-charcoal bg-white" data-testid="close-booking-btn">Close Booking</Button>}
            {event.status === "closed" && <Button variant="outline" size="sm" onClick={() => setStatus("open")} className="border-steel text-charcoal bg-white" data-testid="reopen-booking-btn">Re-open Booking</Button>}
            {!["cancelled", "completed"].includes(event.status) && <Button variant="outline" size="sm" onClick={() => setStatus("cancelled")} className="border-red-300 text-red-700 bg-white hover:bg-red-50" data-testid="cancel-event-btn">Cancel Event</Button>}
            {event.status !== "completed" && <Button variant="outline" size="sm" onClick={() => setStatus("completed")} className="border-steel text-charcoal bg-white" data-testid="complete-event-btn">Mark Completed</Button>}
            <Button variant="outline" size="sm" onClick={openDelete} className="border-red-300 text-red-700 bg-white hover:bg-red-50" data-testid="delete-event-btn">
              <Trash2 className="h-4 w-4 mr-1" /> Delete Event
            </Button>
          </>
        )}
      </div>

      <Dialog open={delOpen} onOpenChange={() => { setDelOpen(false); setDelWarning(null); }}>
        <DialogContent className="bg-white border-steel text-foreground" data-testid="delete-event-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading uppercase text-red-700">Delete Assessment Day</DialogTitle>
          </DialogHeader>
          <div className="bg-elevated border border-steel rounded-sm p-4 grid grid-cols-2 gap-x-6 gap-y-2 text-sm" data-testid="delete-event-facts">
            <div><p className="text-[10px] font-mono uppercase tracking-widest text-slate-500">Title</p><p className="font-semibold text-charcoal" data-testid="del-ev-title">{event.title}</p></div>
            <div><p className="text-[10px] font-mono uppercase tracking-widest text-slate-500">Date</p><p className="text-charcoal" data-testid="del-ev-date">{fmtLong(event.date)}</p></div>
            <div><p className="text-[10px] font-mono uppercase tracking-widest text-slate-500">Time</p><p className="text-charcoal" data-testid="del-ev-time">{event.start_time}–{event.end_time}</p></div>
            <div><p className="text-[10px] font-mono uppercase tracking-widest text-slate-500">Location</p><p className="text-charcoal" data-testid="del-ev-location">{event.location_name || "—"}</p></div>
            <div><p className="text-[10px] font-mono uppercase tracking-widest text-slate-500">Status</p><div data-testid="del-ev-status"><EventStatusPill status={event.effective_status} /></div></div>
            <div><p className="text-[10px] font-mono uppercase tracking-widest text-slate-500">Capacity</p><p className="text-charcoal" data-testid="del-ev-capacity">{event.capacity}</p></div>
            <div><p className="text-[10px] font-mono uppercase tracking-widest text-slate-500">Booked candidates</p><p className="text-charcoal font-semibold" data-testid="del-ev-booked">{event.booked}</p></div>
            <div><p className="text-[10px] font-mono uppercase tracking-widest text-slate-500">Checked-in</p><p className="text-charcoal font-semibold" data-testid="del-ev-checked-in">{arrivedCount}</p></div>
          </div>
          {delWarning && (
            <div className="bg-red-50 border border-red-300 rounded-sm p-3.5" data-testid="del-ev-bookings-warning">
              <p className="text-sm font-semibold text-red-700">This event has booked candidates.</p>
              <p className="text-xs text-red-700 mt-1">{delWarning.booked} booked / waiting · {delWarning.arrived} arrived or completed. Genuine bookings are never silently deleted.</p>
              <ul className="text-xs text-slate-600 mt-2 space-y-1 list-disc pl-4">
                <li>Cancel the event instead — keeps history and notifies candidates, or</li>
                <li>Move / reschedule the candidates to another date first, or</li>
                <li>Delete only after the bookings are cleared.</li>
              </ul>
              {(delWarning.all_test || delWarning.event_cancelled) && (
                <Button onClick={() => submitDelete(true)} disabled={delBusy}
                  className="mt-3 bg-red-600 text-white font-bold hover:bg-red-700" data-testid="del-ev-force-btn">
                  {delBusy ? "Deleting..." : `Permanently delete event + ${event.attendees.length} booking(s)`}
                </Button>
              )}
            </div>
          )}
          <div className="space-y-1.5">
            <p className="text-[10px] font-mono uppercase tracking-widest text-slate-500">Reason (required)</p>
            <Select value={delReason} onValueChange={setDelReason}>
              <SelectTrigger className="bg-white border-steel text-charcoal" data-testid="del-ev-reason"><SelectValue placeholder="Select reason" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="test_event" data-testid="del-ev-reason-test_event">Test event</SelectItem>
                <SelectItem value="duplicate" data-testid="del-ev-reason-duplicate">Duplicate event</SelectItem>
                <SelectItem value="created_in_error" data-testid="del-ev-reason-created_in_error">Created in error</SelectItem>
                <SelectItem value="event_cancelled" data-testid="del-ev-reason-event_cancelled">Event cancelled</SelectItem>
                <SelectItem value="other" data-testid="del-ev-reason-other">Other</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {delReason === "other" && (
            <Input value={delNotes} onChange={(e) => setDelNotes(e.target.value)} placeholder="Notes (required for Other)"
              className="bg-white border-steel text-charcoal" data-testid="del-ev-notes" />
          )}
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => { setDelOpen(false); setDelWarning(null); }} className="border-steel text-charcoal bg-white" data-testid="del-ev-cancel-btn">Cancel</Button>
            {!delWarning && (
              <Button onClick={() => submitDelete(false)} disabled={delBusy || !delReason}
                className="bg-red-600 text-white font-bold hover:bg-red-700" data-testid="del-ev-confirm-btn">
                {delBusy ? "Deleting..." : "Delete Event"}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <section className="bg-charcoal rounded-md p-6 text-white" data-testid="event-header">
        <div className="flex flex-wrap justify-between gap-4">
          <div>
            <div className="hazard-stripes h-1.5 w-20 mb-3 rounded-sm" />
            <h1 className="font-heading font-extrabold uppercase tracking-tight text-xl sm:text-2xl" data-testid="event-title">{event.title}</h1>
            <p className="text-white/80 text-sm mt-1" data-testid="event-when">{fmtLong(event.date)} — {event.start_time}–{event.end_time}</p>
            <p className="text-white/60 text-sm">{event.location_name}{event.address ? ` — ${event.address}` : ""}</p>
            <p className="text-white/60 text-xs mt-1 font-mono uppercase tracking-widest">Assessor: {event.instructor || "TBC"} — Arrive {event.arrival_minutes} min early</p>
          </div>
          <div className="text-right space-y-2">
            <EventStatusPill status={event.effective_status} />
            <div className="grid grid-cols-3 gap-3 text-center">
              <div><p className="font-heading font-black text-2xl text-lomond" data-testid="event-capacity">{event.capacity}</p><p className="text-[9px] font-mono uppercase tracking-widest text-white/50">Capacity</p></div>
              <div><p className="font-heading font-black text-2xl text-white" data-testid="event-booked">{event.booked}</p><p className="text-[9px] font-mono uppercase tracking-widest text-white/50">Booked</p></div>
              <div><p className="font-heading font-black text-2xl text-white" data-testid="event-spaces">{event.spaces_remaining}</p><p className="text-[9px] font-mono uppercase tracking-widest text-white/50">Spaces</p></div>
            </div>
          </div>
        </div>
        {event.all_activities_done && arrivedOrBooked.length > 0 && (
          <div className="mt-4 bg-lomond text-black rounded-sm px-4 py-2.5 font-heading font-bold uppercase tracking-wide text-sm" data-testid="day-complete-banner">
            Assessment day complete — record final outcomes below
          </div>
        )}
      </section>

      <div className="flex items-center justify-between flex-wrap gap-3">
        <h2 className="font-heading font-bold uppercase tracking-wide text-charcoal text-lg" data-testid="attendees-heading">
          Attendees ({event.attendees.length})
        </h2>
        {["management_admin", "funding_admin"].includes(user?.role) && event.event_type !== "external_training" && (
          <Button onClick={openBookDialog} className="bg-lomond text-black font-bold hover:bg-[#E6B400]" data-testid="manual-book-btn">
            <UserPlus className="h-4 w-4 mr-1.5" /> Book Candidate
          </Button>
        )}
      </div>

      <div className="space-y-3" data-testid="attendees-list">
        {event.attendees.map((a) => (
          <div key={a.id} className="bg-white border border-steel rounded-md p-4 sm:p-5" data-testid={`attendee-${a.candidate_id}`}>
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <Link to={`/staff/calendar/${eventId}/candidate/${a.candidate_id}`} className="font-heading font-bold uppercase text-charcoal hover:text-lomond" data-testid={`attendee-name-${a.candidate_id}`}>
                    {a.candidate_name}
                  </Link>
                  <BookingStatusPill status={a.status} />
                  {a.no_show_count > 0 && <span className="text-[10px] font-mono uppercase text-red-600" data-testid={`noshow-count-${a.candidate_id}`}>No-show ×{a.no_show_count}</span>}
                </div>
                <p className="text-xs text-slate-500 mt-0.5">{a.candidate_email} — {a.candidate_mobile || "no mobile"} — {a.local_authority || "LA unknown"} — {a.referral_source || "direct"}</p>
                <p className="text-xs mt-1">Theory: <ScorePill value={a.theory_score} suffix="%" testid={`attendee-theory-${a.candidate_id}`} /></p>
                {a.workstyle_answered >= 12 ? (
                  <p className="text-xs text-slate-600 mt-1.5 bg-lomond/10 border border-lomond/30 rounded-sm px-2.5 py-1.5" data-testid={`interview-focus-${a.candidate_id}`}>
                    <span className="font-mono text-[10px] uppercase tracking-widest text-lomond font-bold">Interview focus: </span>
                    {a.interview_focus}
                  </p>
                ) : (
                  <p className="text-xs text-amber-700 mt-1.5 bg-amber-400/10 border border-amber-300 rounded-sm px-2.5 py-1.5" data-testid={`workstyle-missing-${a.candidate_id}`}>
                    <span className="font-mono text-[10px] uppercase tracking-widest font-bold">No work-style responses on file</span>
                    {" "}— cover work style, learning approach and response to instruction manually during interview.
                  </p>
                )}
                <div className="flex gap-2 mt-2 flex-wrap" data-testid={`activities-${a.candidate_id}`}>
                  <ActivityChip label="Interview" done={a.interview_done} href={`/staff/calendar/${eventId}/candidate/${a.candidate_id}`} testid={`activity-interview-${a.candidate_id}`} />
                  <ActivityChip label="Simulator" done={a.simulator_done} href={`/staff/calendar/${eventId}/candidate/${a.candidate_id}`} testid={`activity-simulator-${a.candidate_id}`} />
                  <ActivityChip label="Decision" done={!!a.final_outcome} href={`/staff/calendar/${eventId}/candidate/${a.candidate_id}`} testid={`activity-review-${a.candidate_id}`} />
                </div>
                {a.check_in_time && (
                  <p className="text-[10px] font-mono uppercase tracking-widest text-emerald-700 mt-2" data-testid={`checkin-${a.candidate_id}`}>
                    <Clock className="h-3 w-3 inline mr-1" />Checked in {new Date(a.check_in_time).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}
                  </p>
                )}
                {a.final_outcome && (
                  <p className="text-[10px] font-mono uppercase tracking-widest text-charcoal mt-2" data-testid={`outcome-${a.candidate_id}`}>
                    Outcome: {a.final_outcome.replace(/_/g, " ")}
                  </p>
                )}
              </div>
              <div className="flex flex-wrap gap-1.5 shrink-0 max-w-md" data-testid={`actions-${a.candidate_id}`}>
                <Link to={`/staff/candidates/${a.candidate_id}`}>
                  <Button size="sm" variant="outline" className="border-steel text-charcoal bg-white h-8 text-xs" data-testid={`open-profile-${a.candidate_id}`}>Open Profile</Button>
                </Link>
                <a href={`mailto:${a.candidate_email}`}><Button size="sm" variant="outline" className="border-steel text-charcoal bg-white h-8 text-xs" data-testid={`email-${a.candidate_id}`}><Mail className="h-3.5 w-3.5" /></Button></a>
                {waLink(a.candidate_mobile) && <a href={waLink(a.candidate_mobile)} target="_blank" rel="noreferrer"><Button size="sm" variant="outline" className="border-steel text-charcoal bg-white h-8 text-xs" data-testid={`whatsapp-${a.candidate_id}`}><MessageSquare className="h-3.5 w-3.5" /></Button></a>}
                {a.candidate_mobile && <a href={`tel:${a.candidate_mobile}`}><Button size="sm" variant="outline" className="border-steel text-charcoal bg-white h-8 text-xs" data-testid={`call-${a.candidate_id}`}><Phone className="h-3.5 w-3.5" /></Button></a>}
                {a.status === "booked" && (
                  <Button size="sm" onClick={() => action(a.id, "mark_arrived")} className="bg-emerald-600 text-white hover:bg-emerald-500 h-8 text-xs font-bold" data-testid={`mark-arrived-${a.candidate_id}`}>Mark Arrived</Button>
                )}
                {["booked", "arrived"].includes(a.status) && (
                  <>
                    <Button size="sm" variant="outline" onClick={() => action(a.id, "no_show")} className="border-red-300 text-red-700 bg-white hover:bg-red-50 h-8 text-xs" data-testid={`no-show-${a.candidate_id}`}>No Show</Button>
                    <Button size="sm" variant="outline" onClick={() => openReschedule(a)} className="border-steel text-charcoal bg-white h-8 text-xs" data-testid={`reschedule-${a.candidate_id}`}>Reschedule</Button>
                    <Button size="sm" variant="outline" onClick={() => { if (window.confirm("Cancel this booking? The slot will be released.")) action(a.id, "cancel"); }} className="border-steel text-slate-500 bg-white h-8 text-xs" data-testid={`cancel-booking-${a.candidate_id}`}>Cancel</Button>
                  </>
                )}
                {a.status === "arrived" && isManagement && !a.final_outcome && (
                  <Link to={`/staff/calendar/${eventId}/candidate/${a.candidate_id}`}>
                    <Button size="sm" className="bg-lomond text-black hover:bg-[#E6B400] h-8 text-xs font-bold" data-testid={`record-outcome-${a.candidate_id}`}>Open Day Record</Button>
                  </Link>
                )}
                {a.status === "waiting" && ["management_admin", "funding_admin"].includes(user?.role) && (
                  <>
                    <Button size="sm" onClick={() => action(a.id, "offer_place")} className="bg-lomond text-black hover:bg-[#E6B400] h-8 text-xs font-bold" data-testid={`offer-place-${a.candidate_id}`}>Offer Place</Button>
                    <Button size="sm" variant="outline" onClick={() => action(a.id, "remove_waitlist")} className="border-steel text-slate-500 bg-white h-8 text-xs" data-testid={`remove-waitlist-${a.candidate_id}`}>Remove</Button>
                  </>
                )}
                {a.status === "no_show" && ["management_admin", "funding_admin"].includes(user?.role) && (
                  <Button size="sm" variant="outline" onClick={() => openReschedule(a)} className="border-steel text-charcoal bg-white h-8 text-xs" data-testid={`offer-new-date-${a.candidate_id}`}>Offer New Date</Button>
                )}
              </div>
            </div>
          </div>
        ))}
        {!event.attendees.length && (
          <div className="bg-white border border-steel rounded-md p-10 text-center" data-testid="no-attendees">
            <UserPlus className="h-8 w-8 text-slate-300 mx-auto mb-3" />
            <p className="text-sm text-slate-500">No attendees booked yet. Invite candidates, then book them in.</p>
          </div>
        )}
      </div>

      <EventForm open={editOpen} onClose={() => setEditOpen(false)} event={event} onSaved={load} />

      <Dialog open={bookOpen} onOpenChange={setBookOpen}>
        <DialogContent className="bg-white border-steel text-foreground" data-testid="manual-book-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading uppercase text-charcoal">Book Candidate Manually</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-500">Capacity {event.capacity} — booked {event.booked} — spaces {event.spaces_remaining}. Booking sends a portal confirmation automatically.</p>
          <Select value={bookCandidate} onValueChange={setBookCandidate}>
            <SelectTrigger className="bg-white border-steel text-charcoal" data-testid="manual-book-candidate"><SelectValue placeholder="Select candidate" /></SelectTrigger>
            <SelectContent>
              {candidates.map((c) => <SelectItem key={c.id} value={c.id} data-testid={`book-option-${c.id}`}>{c.name} — {c.stage_label}</SelectItem>)}
            </SelectContent>
          </Select>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setBookOpen(false)} className="border-steel text-charcoal bg-white" data-testid="manual-book-cancel">Cancel</Button>
            <Button onClick={() => manualBook(false)} className="bg-lomond text-black font-bold hover:bg-[#E6B400]" data-testid="manual-book-confirm">Book Candidate</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!rescheduleFor} onOpenChange={() => setRescheduleFor(null)}>
        <DialogContent className="bg-white border-steel text-foreground" data-testid="reschedule-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading uppercase text-charcoal">Reschedule — {rescheduleFor?.candidate_name}</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-500">The current slot is released immediately and the candidate is moved to the new date.</p>
          <Select value={rescheduleEvent} onValueChange={setRescheduleEvent}>
            <SelectTrigger className="bg-white border-steel text-charcoal" data-testid="reschedule-event-select"><SelectValue placeholder="Select new assessment day" /></SelectTrigger>
            <SelectContent>
              {openEvents.map((e) => <SelectItem key={e.id} value={e.id} data-testid={`reschedule-option-${e.id}`}>{fmtLong(e.date)} — {e.start_time} — {e.spaces_remaining} spaces</SelectItem>)}
            </SelectContent>
          </Select>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setRescheduleFor(null)} className="border-steel text-charcoal bg-white" data-testid="reschedule-cancel">Cancel</Button>
            <Button onClick={submitReschedule} className="bg-lomond text-black font-bold hover:bg-[#E6B400]" data-testid="reschedule-confirm">Confirm Reschedule</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <div id="day-sheet-print" className="hidden" aria-hidden="true" data-testid="day-sheet-print">
        <h1>{event.title}</h1>
        <p className="sheet-meta">
          {fmtLong(event.date)} — {event.start_time}–{event.end_time} | {event.location_name}{event.address ? ` — ${event.address}` : ""} | Assessor: {event.instructor || "TBC"} | Arrive {event.arrival_minutes} min early | {arrivedOrBooked.length} candidate{arrivedOrBooked.length === 1 ? "" : "s"}
        </p>
        {arrivedOrBooked.map((a) => (
          <div key={a.candidate_id} className="sheet-attendee" data-testid={`day-sheet-attendee-${a.candidate_id}`}>
            <p className="sheet-name">
              {a.candidate_name}
              <span className="sheet-detail"> — {a.status.toUpperCase()} — Theory: {a.theory_score != null ? `${a.theory_score}%` : "—"} — {a.candidate_mobile || "no mobile"}</span>
            </p>
            {a.workstyle_answered >= 12 ? (
              <p className="sheet-focus" data-testid={`day-sheet-focus-${a.candidate_id}`}>
                <strong>Interview Focus:</strong> {a.interview_focus}
              </p>
            ) : (
              <p className="sheet-focus sheet-warn" data-testid={`day-sheet-noworkstyle-${a.candidate_id}`}>
                <strong>⚠ NO WORK-STYLE RESPONSES ON FILE</strong><br />
                Cover work style, learning approach and response to instruction manually during interview.
              </p>
            )}
          </div>
        ))}
        {!arrivedOrBooked.length && <p className="sheet-meta">No booked or arrived candidates.</p>}
      </div>

    </div>
  );
}
